/**
 * The kinds of work this product does, and which tabs belong to each.
 *
 * Accounting and valuation are different disciplines, not two halves of one
 * screen. The backend already says so and enforces it: `app/valuation/v2` may
 * read the accounting engine, the accounting engine may not read it, and the one
 * documented crossing is pinned by a test. The reason recorded there is not
 * tidiness - the accounting engine has a REFERENCE to check against, Mike's
 * workbook, with 23 lines tied to it, and the valuation engine has none. Keeping
 * them apart stops the second sheltering inside the first's reputation for being
 * well tested.
 *
 * The frontend grouped the tabs under two headings and stopped there, so both
 * sets were always on screen and the distinction was cosmetic. This makes it
 * structural: a discipline is chosen first, and only its tabs exist.
 *
 * A LIST rather than a pair of branches, because two more are expected. Adding
 * one means adding an entry here and a folder of views - not finding every place
 * that said `isValuation ? ... : ...`.
 */

export type DisciplineId = 'accounting' | 'valuation' | 'm-accounting' | 'diligence' | 'books';

export interface Discipline {
  id: DisciplineId;
  /** On the chooser card and in the header once chosen. */
  label: string;
  /** One line under the label. What the discipline is FOR, not what it contains. */
  blurb: string;
  /**
   * Which of `V2Workspace`'s tab groups this discipline shows, by their IDS.
   *
   * By id rather than label because both disciplines have a group labelled
   * "Analysis" and the two hold different tabs. `admin` - Versions and Settings -
   * belongs to both, because a model has one history whichever discipline is
   * looking at it; its Settings tab shows a different page in each.
   */
  tabGroups: string[];
  /** Where the workspace opens. */
  defaultTab: string;
  /**
   * 'model' (the default) shows the discipline inside V2Workspace on an accounting
   * model, picking `tabGroups` from it. 'own' means the discipline brings its own
   * projects and workspace and claims none of V2Workspace's groups - M-accounting,
   * whose figures must never be an IAS model's.
   */
  workspace?: 'model' | 'own';
}

export const DISCIPLINES: Discipline[] = [
  {
    id: 'accounting',
    label: 'IAS accounting',
    blurb:
      'Build and check a set of statements: profit and loss, balance sheet, cash flow, and the schedules behind them.',
    tabGroups: ['accounts', 'accounting-analysis', 'admin'],
    defaultTab: 'overview',
  },
  {
    id: 'valuation',
    label: 'Valuation',
    blurb:
      'Work out what a business is worth, from finished statements: discounted cash flow, multiples, and what a different cost of capital would do to the answer.',
    tabGroups: ['valuation', 'valuation-analysis', 'admin'],
    defaultTab: 'valuation-overview',
  },
  {
    // Mike's own model: his workbook's tabs and formulas, corrected, with no IAS
    // lines. Kept apart from Accounting on purpose - its own projects, its own
    // workspace (views/v2/m) - so the two models never borrow each other's figures.
    id: 'm-accounting',
    label: 'Baseline Accounting',
    blurb:
      'The baseline model: its own tabs, rows and formulas, for a set of statements without the IAS lines.',
    tabGroups: [],
    defaultTab: 'm-overview',
    workspace: 'own',
  },
  {
    // Due diligence: findings from a company's pinned statements, each with its source.
    // Its own projects and workspace (views/v2/diligence), like Baseline: a diligence
    // project reads an accounting model's statements once, pinned, and never borrows its
    // tabs. See docs/due-diligence-plan.md.
    id: 'diligence',
    label: 'Due Diligence',
    blurb:
      'Preliminary findings from a set of statements: net debt, working capital, cash conversion, quality of earnings and red flags, each with its source.',
    tabGroups: [],
    defaultTab: 'dd-overview',
    workspace: 'own',
  },
  {
    // Books: a company's books kept and proved - the standalone Books product's screens
    // (@mike-spreadsheet/books-ui) inside the Valuator, signed in by the Valuator's own login
    // (single sign-on). Its own companies and workspace (views/v2/books), on Books's API; a
    // company's accounts reach Valuation and Due Diligence only through its filing, imported
    // like any other. See docs/books-in-valuator.md.
    id: 'books',
    label: 'Books',
    blurb:
      "Keep a company's books and close its year: bank, sales, purchases, VAT and the accounts, every figure vouched for - then value it or run due diligence on it.",
    tabGroups: [],
    defaultTab: 'books-companies',
    workspace: 'own',
  },
];

export function disciplineFor(id: string | null): Discipline | undefined {
  return DISCIPLINES.find((entry) => entry.id === id);
}

/**
 * Where the chosen discipline is remembered.
 *
 * Remembered rather than asked every time: a user who does accounting all day
 * should not answer the same question at every visit. It is a preference, not
 * data, so it lives in the browser - and a stale or absent value simply shows the
 * chooser again, which is why nothing here needs migrating.
 */
export const DISCIPLINE_KEY = 'v2.discipline';

export function readDiscipline(): DisciplineId | null {
  try {
    const stored = window.localStorage.getItem(DISCIPLINE_KEY);
    return disciplineFor(stored)?.id ?? null;
  } catch {
    // Private browsing, blocked storage, or a test environment without it. The
    // chooser is the right thing to show when we cannot tell.
    return null;
  }
}

export function writeDiscipline(id: DisciplineId | null): void {
  try {
    if (id === null) window.localStorage.removeItem(DISCIPLINE_KEY);
    else window.localStorage.setItem(DISCIPLINE_KEY, id);
  } catch {
    // Losing the preference is not worth failing a click over.
  }
}
